import React, { useState, useCallback, useEffect } from 'react';
import './Search2.css';
import { API_BASE } from '../config';

function Search2() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');
  const [exportStatus, setExportStatus] = useState('');
  const [exportJobId, setExportJobId] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [pubmedInfo, setPubmedInfo] = useState(null); // To store PubMed count and date range
  const [mysqlCount, setMysqlCount] = useState(null); // To store local MySQL count

  // Perform async search by PubMed query
  const handleSearch = useCallback(async () => {
    if (!query.trim()) {
      setError('Please enter a search term.');
      return;
    }
    setLoading(true);
    setError(null);
    setResults([]);
    setProcessingStatus('Fetching PubMed count...');
    setPubmedInfo(null);
    setMysqlCount(null);

    try {
      // Step 1: Fetch PubMed count first
      const countResponse = await fetch(`${API_BASE}/api/pubmed/search_count?q=${encodeURIComponent(query)}`);
      if (!countResponse.ok) {
        throw new Error('Failed to fetch PubMed count.');
      }
      
      const countData = await countResponse.json();
      const { count, start_date, end_date } = countData;
      
      // Debug logging
      console.log('PubMed count data:', countData);
      console.log('PubMed count:', count);
      
      // Set PubMed info for display
      setPubmedInfo({ count, start_date, end_date });
      
      if (count === 0) {
        setProcessingStatus('No articles found for this query.');
        setLoading(false);
        return;
      }
      
      setProcessingStatus(`Found ${count} articles in PubMed. Starting download...`);

      // Step 2: Start async search job
      const jobResponse = await fetch(`${API_BASE}/api/search2_async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });

      if (!jobResponse.ok) {
        const errorData = await jobResponse.json();
        throw new Error(errorData.error || 'Failed to start search job.');
      }

      const { job_id } = await jobResponse.json();
      setProcessingStatus(`Search job started. Downloading ${count} articles...`);

      // Step 3: Poll for job completion
      let pollCount = 0;
      const maxPolls = 120; // 10 minutes with 5s interval
      const pollInterval = 5000;
      
      const pollJob = async () => {
        if (pollCount >= maxPolls) {
          throw new Error('Search job timed out.');
        }
        
        const statusResponse = await fetch(`${API_BASE}/api/search_jobs/${job_id}`);
        if (!statusResponse.ok) {
          const errorData = await statusResponse.json();
          throw new Error(errorData.error || 'Failed to get job status.');
        }
        
        const statusData = await statusResponse.json();
        const { status, processed, total, results: jobResults } = statusData;
        
        // Debug logging
        console.log('Job status data:', statusData);
        console.log('Job results length:', jobResults?.length || 0);
        console.log('Job results sample:', jobResults?.slice(0, 3) || 'No results');
        
        if (status === 'RUNNING' || status === 'PENDING') {
          setProcessingStatus(`Processing: ${processed}/${total} articles...`);
          pollCount++;
          setTimeout(pollJob, pollInterval);
        } else if (status === 'COMPLETED') {
          setResults(jobResults || []);
          const displayCount = jobResults?.length || 0;
          const totalCount = total ?? displayCount;
          console.log('Setting display count to:', displayCount, 'total', totalCount);
          
          // Set MySQL count
          setMysqlCount(displayCount);
          
          if (displayCount === totalCount) {
            setProcessingStatus(`Displaying ${totalCount} articles.`);
          } else {
            setProcessingStatus(`Displaying ${displayCount} of ${totalCount} articles.`);
          }
          setLoading(false);
        } else if (status === 'FAILED') {
          throw new Error(statusData.error || 'Search job failed.');
        } else {
          throw new Error(`Unknown job status: ${status}`);
        }
      };
      
      // Start polling
      pollJob();
      
    } catch (err) {
      setError(err.message);
      setProcessingStatus('An error occurred.');
      setLoading(false);
    }
  }, [query]);

  // Trigger an async CSV export of the current query via backend list → job workflow
  const handleExport = useCallback(async () => {
    if (!query.trim()) {
      setError('Run a search first before exporting.');
      return;
    }
    if (exportJobId) {
      // Already exporting or completed, ignore multiple clicks
      return;
    }
    try {
      setExportStatus('Creating list…');
      // 1. Create a new list named after the query + timestamp
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const listResp = await fetch(`${API_BASE}/api/lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ list_name: `${query} ${ts}`.slice(0, 250) })
      });
      if (!listResp.ok) throw new Error('Failed to create list');
      const { list_id } = await listResp.json();

      // 2. Attach the current query to that list
      setExportStatus('Registering query…');
      const qResp = await fetch(`${API_BASE}/api/lists/${list_id}/queries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query_string: query })
      });
      if (!qResp.ok) throw new Error('Failed to register query');

      // 3. Kick off async export job
      setExportStatus('Starting export…');
      const exportResp = await fetch(`${API_BASE}/api/lists/${list_id}/export_csv_async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (!exportResp.ok) throw new Error('Failed to start export job');
      const { job_id } = await exportResp.json();
      setExportJobId(job_id);
      setExportStatus('Export in progress…');
    } catch (err) {
      setError(err.message);
      setExportStatus('');
    }
  }, [query, exportJobId]);

  // Poll job status every 5 s while a job is active
  useEffect(() => {
    if (!exportJobId) return;
    const interval = setInterval(async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/download_jobs/${exportJobId}`);
        if (!resp.ok) throw new Error('Failed to poll job status');
        const data = await resp.json();
        if (data.status === 'COMPLETED') {
          setExportStatus('Export completed.');
          setDownloadUrl(`${API_BASE}${data.download_url}`);
          clearInterval(interval);
        } else {
          setExportStatus(`Export status: ${data.status}…`);
        }
      } catch (err) {
        // stop polling on error
        setError('Error while checking export job.');
        clearInterval(interval);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [exportJobId]);

  const clearResults = () => {
    setResults([]);
    setQuery('');
    setProcessingStatus('');
    setError(null);
  };

  return (
    <div className="search2-container">
      <div className="search2-header">
        <h1>Live PubMed Search</h1>
        <p className="search-info">
          This search fetches live results from PubMed and displays corresponding articles from our database.
        </p>
      </div>

      <div className="search-controls">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter PubMed search term"
          className="search-input"
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          className="search-button"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
        <button
          onClick={clearResults}
          className="clear-button"
          style={{marginLeft: '10px'}}
        >
          Clear Results
        </button>
      </div>

      <div className="results-section">
        {pubmedInfo && (
          <div className="status-message">
            PubMed found {pubmedInfo.count} articles (date range: {pubmedInfo.start_date} to {pubmedInfo.end_date})
          </div>
        )}
        
        {mysqlCount !== null && (
          <div className="status-message">
            Local database contains {mysqlCount} of these articles
          </div>
        )}

        {processingStatus && (
          <div className="status-message">
            {processingStatus}
          </div>
        )}

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {results.length > 0 && (
          <div className="results-header" style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
            <h2>Search Results ({results.length})</h2>
            <button
              className="export-button"
              onClick={handleExport}
              disabled={!!exportJobId || loading}
            >
              {exportJobId ? 'Exporting…' : 'Export Authors CSV'}
            </button>
          </div>
        )}

        <div className="results-grid">
          {results.map((article) => (
            <div key={article.pmid} className="result-card">
              <div className="result-title">
                <a href={`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}`} target="_blank" rel="noopener noreferrer">
                  {article.title}
                </a>
              </div>
              <div className="result-authors">
                {article.authors?.join(', ') || 'No authors listed'}
              </div>
              <div className="result-journal">
                {article.journal} ({article.year})
              </div>
              <div className="result-pmid">
                PMID: {article.pmid}
              </div>
              <div className="result-abstract">
                {article.abstract?.substring(0, 200)}...
              </div>
            </div>
          ))}
        </div>

        {exportStatus && (
          <div className="status-message" style={{marginTop: '10px'}}>
            {exportStatus}
            {downloadUrl && (
              <span style={{marginLeft: '1rem'}}>
                <a href={downloadUrl} download>Download CSV</a>
              </span>
            )}
          </div>
        )}

        {results.length === 0 && !loading && !error && processingStatus && (
          <div className="no-results">
            No articles found for your search query.
          </div>
        )}
      </div>
    </div>
  );
}


export default Search2;
